import { QUERY_KEYS } from '@/constants';
import type { IPage } from '@ai-indeed/reader/dist/type';
import { postTransitionTask } from '@ai-indeed/scheduler';
import { request, useQuery } from '@umijs/max';
import { Dropdown, Progress } from 'antd';
import type { ReactNode, UIEventHandler } from 'react';
import {
  forwardRef,
  startTransition,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { IconButton } from '../IconButton';
import { IconFont } from '../IconFont';
import { Spin } from '../Spin';
import {
  DATA_DIRTY,
  DATA_INDEX,
  DATA_INTERSECTING,
  DATA_NUM,
  DATA_RAW,
  zoomValueList,
} from './const';
import { useLatest, useReader } from './hooks';
import './index.less';
import type { IPageInfo, IPdfRef } from './type';
import { sleep } from 'radash';

export const PdfRenderer = forwardRef<
  IPdfRef,
  {
    src: string;
  } & Partial<{
    pure: boolean;
    pageInfoSrc: string;
    defaultPageNumber: number;
    onPageCreated: (page: IPage, pageInfo: IPageInfo) => void;
    onPageNumberChange: (pageNumber: number) => void;
    onScroll: UIEventHandler<HTMLElement>;
    onResize: () => void;
    renderSearcher: () => ReactNode;
  }>
>((props, ref) => {
  // props
  const {
    src,
    pure,
    pageInfoSrc,
    defaultPageNumber = 1,
    onPageCreated,
    onPageNumberChange,
    onScroll,
    onResize,
    renderSearcher,
  } = props;
  // state
  const [current, setCurrent] = useState(defaultPageNumber);
  const [total, setTotal] = useState(-1);
  const [zoomValue, setZoomValue] = useState(1);
  const [progress, setProgress] = useState(0);
  // ref
  const rootRef = useRef<HTMLElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const runtimeRef = useRef<{
    pageInfoList: IPageInfo[];
    width: number;
  }>({
    pageInfoList: [],
    width: -1,
  });
  const pageCreatedRef = useLatest(onPageCreated);
  const pageNumberChangeRef = useLatest(onPageNumberChange);
  // reader
  const reader = useReader(
    (reader, { onPDFDocumentProxyCreated, onProgress, onError }) => {
      reader.read({
        url: src,
        rangeChunkSize: 1024 * 128,
        cMapUrl: 'https://cdn-static-resources.ai-indeed.com/supertext/cmaps/',
      });

      onPDFDocumentProxyCreated((docProxy) => {
        setTotal(docProxy.numPages);
      });

      onProgress((e) => {
        setProgress(e.loaded / e.total);
      });

      onError((err) => console.error('pdf renderer error: ', err));
    },
  );
  // abort controller
  const acRef = useRef<AbortController | null>(null);
  // queries
  const {
    data: pageInfoList,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.pageInfo, pageInfoSrc],
    queryFn: async ({ queryKey, signal }) => {
      return request<Record<number, IPageInfo>>(queryKey[1] as string, {
        signal,
      }).then((pageInfoMap) => {
        const pageInfoList = [] as IPageInfo[];
        for (const index in pageInfoMap) {
          if (!Object.prototype.hasOwnProperty.call(pageInfoMap, index)) {
            continue;
          }
          pageInfoList[+index - 1] = pageInfoMap[index];
        }
        setTotal(pageInfoList.length);
        return pageInfoList;
      });
    },
    refetchOnWindowFocus: false,

    enabled: !pure,
  });
  // methods
  const recreate = (width: number) => {
    if (acRef.current) {
      acRef.current.abort();
    }
    const ac = (acRef.current = new AbortController());
    postTransitionTask(
      () => {
        reader.getPages().forEach((page) => {
          const { pageNumber, width: currentWidth } = page;
          if (currentWidth === width) {
            return;
          }
          const container = page.container!;
          if (container.hasAttribute(DATA_INTERSECTING)) {
            reader.createPage(container, pageNumber, width);
          } else {
            container.setAttribute(DATA_DIRTY, '');
          }
        });
      },
      {
        signal: ac.signal,
      },
    );
  };
  const getBoundingClientRect = (pageNumber: number) => {
    const scroller = scrollerRef.current!;
    const target = scroller.querySelector(
      `section[${DATA_NUM}="${pageNumber}"]`,
    )!;
    return target.getBoundingClientRect();
  };
  const turn = (pageNumber: number, behavior: ScrollBehavior = 'smooth') => {
    if (pageNumber > total) {
      return;
    }
    if (acRef.current) {
      acRef.current.abort();
    }
    const ac = (acRef.current = new AbortController());
    postTransitionTask(
      () => {
        const root = rootRef.current!;
        const { top } = getBoundingClientRect(pageNumber);
        root.scrollBy({
          top: top - 80,
          behavior,
        });
      },
      { signal: ac.signal },
    );
  };
  const zoom = (value: number) => {
    setZoomValue(() => {
      const root = rootRef.current!;
      const scroller = scrollerRef.current!;
      scroller.style.width = runtimeRef.current.width * value + 'px';
      recreate(root.getBoundingClientRect().width * value);
      return value;
    });
  };
  // imperative handle
  useImperativeHandle(ref, () => ({
    getPages() {
      return reader.getPages();
    },
    create: (pageNumber) => {
      const scroller = scrollerRef.current!;
      const container = scroller.querySelector(
        `section[${DATA_NUM}="${pageNumber}"]`,
      )!;
      const page = reader.createPage(
        container as HTMLElement,
        pageNumber,
        runtimeRef.current.width,
      );
      container.removeAttribute(DATA_RAW);
      container.removeAttribute(DATA_DIRTY);
      return page;
    },
    turn: (pageNumber: number, behavior: ScrollBehavior = 'auto') => {
      turn(pageNumber, behavior);
    },
  }));
  // effect
  useEffect(() => {
    if (pageInfoList) {
      const unsubscribe = reader.subscribe((page) => {
        const pageCreated = pageCreatedRef.current;

        if (typeof pageCreated === 'function') {
          pageCreated(page, pageInfoList[page.pageNumber - 1]);
        }
      });
      return () => {
        unsubscribe();
      };
    }
  }, [pageInfoList]);
  useEffect(() => {
    if (pageInfoList) {
      if (defaultPageNumber !== 1) {
        turn(defaultPageNumber, 'auto');
      }
    }
  }, [pageInfoList]);
  useEffect(() => {
    let ac: AbortController;
    const root = rootRef.current!;
    const scroller = scrollerRef.current!;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const { target } = entry;
          const pageNumber = target.getAttribute(DATA_NUM)!;
          if (entry.isIntersecting) {
            if (
              target.hasAttribute(DATA_RAW) ||
              target.hasAttribute(DATA_DIRTY) ||
              reader.getPage(+pageNumber) === null
            ) {
              const { boundingClientRect } = entry;
              const { width, height } = boundingClientRect;

              if (ac) {
                ac.abort();
              }
              postTransitionTask(
                () => {
                  reader.createPage(
                    target as HTMLElement,
                    +pageNumber,
                    width,
                    height,
                  );
                  target.removeAttribute(DATA_RAW);
                  target.removeAttribute(DATA_DIRTY);
                },
                { signal: (ac = new AbortController()).signal },
              );
            } else {
              if (acRef.current) {
                acRef.current.abort();
              }
            }
            target.setAttribute(DATA_INTERSECTING, '');
            startTransition(() => {
              const nextPageNumber = +pageNumber;
              const pageNumberChange = pageNumberChangeRef.current;
              if (typeof pageNumberChange === 'function') {
                pageNumberChange(nextPageNumber);
              }
              setCurrent(nextPageNumber);
            });
          } else {
            target.removeAttribute(DATA_INTERSECTING);
          }
        });
      },
      { root, threshold: 0.2 },
    );
    const mo = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const { addedNodes, removedNodes } = mutation;
        addedNodes.forEach((node) => {
          if (node.nodeType !== 1) {
            return;
          }
          const element = node as HTMLElement;
          const index = element.getAttribute(DATA_INDEX);
          if (element.getAttribute(DATA_NUM)) {
            io.observe(element);
          }
          if (index) {
            const pageNumber = +index + 1;
            if ([1, 2, 3].includes(pageNumber)) {
              reader.createPage(element, pageNumber);
            }
          }
        });
        removedNodes.forEach((node) => {
          const element = node as Element;
          if (element.getAttribute(DATA_NUM)) {
            io.unobserve(element);
          }
        });
      }
    });
    const ro = new ResizeObserver((entries) => {
      const { contentRect } = entries[0];
      const width = (runtimeRef.current.width = contentRect.width);

      if (typeof onResize === 'function') {
        onResize();
      }

      scroller.style.width = width * zoomValue + 'px';
      recreate(width);
    });
    if (pageInfoList) {
      Array.prototype.forEach.call(scroller.children, (child) => {
        if (child.hasAttribute(DATA_NUM)) {
          io.observe(child);
        }
      });
    }
    mo.observe(scroller, {
      childList: true,
      attributes: false,
      subtree: false,
    });
    ro.observe(root);

    return () => {
      io.disconnect();
      ro.disconnect();
      mo.disconnect();
    };
  }, []);
  useEffect(() => () => reader.destroy(), []);

  return (
    <section className="relative h-full">
      <main
        className="flex justify-center bg-main-bg h-full overflow-x-scroll overlay-y"
        ref={rootRef}
        onScroll={onScroll}
      >
        <div ref={scrollerRef} className="overlay-y" onScroll={onScroll}>
          {pure ? (
            total === -1 ? (
              <section className="flex flex-col justify-center items-center gap-2 bg-white h-full">
                <Spin width={42} height={42} />
                <p className="text-secondary-2 text-sm">解析中</p>
              </section>
            ) : (
              <>
                {Array.from({ length: total }).map((_, idx) => {
                  return (
                    <section
                      className="relative flex justify-center items-center bg-white hover:shadow-sm mb-0.5 pr-page pr-page--pure rounded w-full transition"
                      key={idx}
                      style={{ aspectRatio: 210 / 297 }}
                      data-index={idx}
                      data-num={idx + 1}
                    >
                      <Spin width={24} height={24} />
                    </section>
                  );
                })}
              </>
            )
          ) : isLoading || isFetching ? (
            <section className="flex flex-col justify-center items-center gap-4 bg-white h-full">
              <Progress
                className="w-1/3"
                percent={progress * 100}
                showInfo={false}
              />
              <p className="text-secondary-1 text-sm">
                文件加载中（{(progress * 100).toFixed(2)}%）…
              </p>
            </section>
          ) : (
            pageInfoList &&
            pageInfoList.map((page) => {
              const { num, width, height } = page;
              return (
                <section
                  data-raw
                  className="relative flex justify-center items-center bg-white hover:shadow-sm mb-0.5 pr-page rounded transition"
                  key={num}
                  style={{ aspectRatio: width / height }}
                  data-num={num}
                >
                  <Spin width={24} height={24} />
                </section>
              );
            })
          )}
        </div>
      </main>
      <footer className="right-0 bottom-0 left-0 absolute flex justify-between items-center backdrop-blur px-4 pr-footer h-8">
        <section className="flex items-center gap-6">
          <div className="flex items-center gap-1">
            <IconButton
              circle
              title="上一页"
              className="disabled:text-secondary-3 text-xs"
              iconType="icon-xiangzuo"
              disabled={current === 1}
              onClick={() => {
                setCurrent((value) => {
                  const next = value - 1;
                  turn(next);
                  return next;
                });
              }}
            />
            <div className="text-[13px] text-main">
              <span className="inline-block border-b border-b-secondary-5 w-4 text-black text-center">
                {current}
              </span>{' '}
              / {total}
            </div>
            <IconButton
              circle
              title="下一页"
              className="disabled:text-secondary-3 text-xs"
              iconType="icon-xiangyou"
              disabled={pageInfoList ? current === pageInfoList.length : true}
              onClick={() => {
                setCurrent((value) => {
                  const next = value + 1;
                  turn(next);
                  return next;
                });
              }}
            />
          </div>
          {!pure && typeof renderSearcher === 'function' && renderSearcher()}
        </section>
        <section className="flex justify-end items-center gap-0.5">
          <IconButton
            circle
            title="缩小"
            className="disabled:text-secondary-3 text-xs"
            iconType="icon-suoxiao"
            disabled={zoomValue <= 0.5}
            onClick={() => zoom(zoomValue - 0.25)}
          />
          <Dropdown
            placement="top"
            menu={{
              selectable: true,
              selectedKeys: [`${zoomValue}`],
              items: zoomValueList.map((value) => ({
                key: value,
                itemIcon: value === zoomValue && (
                  <IconFont
                    className="ml-1 text-primary"
                    type="icon-xuanzhong"
                  />
                ),
                label: `${value * 100}%`,
              })),
              onClick: ({ key }) => zoom(+key),
            }}
          >
            <p className="text-main text-xs">{zoomValue * 100}%</p>
          </Dropdown>
          <IconButton
            circle
            title="放大"
            className="disabled:text-secondary-3 text-xs"
            iconType="icon-fangda"
            disabled={zoomValue >= 2}
            onClick={() => zoom(zoomValue + 0.25)}
          />
        </section>
      </footer>
    </section>
  );
});

PdfRenderer.displayName = 'PdfRenderer';

export type { IPdfRef } from './type';
